import React, { useEffect, useState } from 'react';
import { Database, Globe, Settings, Shield } from 'lucide-react';
import URLControlTab from './tabs/URLControlTab';
import PrivilegeManagementTab from './tabs/PrivilegeManagementTab';
import DataAccessTab from './tabs/DataAccessTab';
import ApplicationControlTab from './tabs/ApplicationControlTab';
import { Device } from '../interfaces/EndpointInterfaces';
import useUserStore from '../../../services/userStore';
import { myLocalStorage } from '../../StorageHelper';
import { getUsers } from './Api/resourceApi';
import '../CloudAnalytics/CloudEntityAnalytics.css';
import './EndpointResources.css';

interface ResourceDetailsProps {
  device: Device;
}

const tabs = [
  { id: "app-control", name: "Application control", icon: Settings },
  { id: "data-access", name: "Data access", icon: Database },
  { id: "privilege-mgmt", name: "Privilege management", icon: Shield },
  { id: "url-control", name: "URL control", icon: Globe }
];

export default function ResourceDetails({ device }: ResourceDetailsProps) {
  const [activeTab, setActiveTab] = useState("app-control");
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [users, setUsers] = useState<any[]>([]);

  const { email, role, userRole } = useUserStore((state) => state.user);
  const tenantName = myLocalStorage.getItem("latestTenant").tenantName || "";
  const isOwnComputer = device?.belongedToUser === email;

  const isDeviceSSHBased = (device: Device) =>
    device?.OperatingSystem?.toLowerCase().includes("linux");

  const handleTabChange = (tabId: string) => setActiveTab(tabId);
  const handleUserSelect = (userId: string | null) => setSelectedUser(userId);

  const fetchUsers = async (device: Device) => {
    const isSSHBased = !isDeviceSSHBased(device);
    try {
      const response = await getUsers(
        email,
        tenantName,
        device?.id.toString(),
        role,
        isOwnComputer,
        true,
        userRole,
        isSSHBased,
        []
      );

      if (response?.length) {
        setUsers(response);
        setSelectedUser(response[0].id.toString());
      }
    } catch (err) {
      console.error("Failed to fetch users:", err);
    }
  };

  useEffect(() => {
    if (device?.id) fetchUsers(device);
  }, [device?.id]);

  return (
    <div className="endpoint-resources">
      <div className="cea-tabs endpoint-resource-tabs" aria-label="Resource sections">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`cea-tab ${activeTab === tab.id ? 'active' : ''}`}
            aria-pressed={activeTab === tab.id}
            data-testid={`tab-${tab.id}`}
          >
            <div className="flex items-center gap-2">
              <tab.icon className="w-4 h-4" />
              {tab.name}
            </div>
          </button>
        ))}
      </div>


      <div className="endpoint-resource-content">
        {activeTab === "app-control" && (
          <ApplicationControlTab
            users={users}
            selectedUser={String(selectedUser)}
            onUserSelect={handleUserSelect}
            device={device}
            profileId={-1}
            os={"windows"}
            fromDetails={false}
            showEmptyProperties
          />
        )}
        {activeTab === "data-access" && (
          <DataAccessTab
            users={users}
            selectedUser={String(selectedUser)}
            onUserSelect={handleUserSelect}
            device={device}
            profileId={-1}
            os={"windows"}
          />
        )}
        {activeTab === "privilege-mgmt" && (
          <PrivilegeManagementTab
            users={users}
            selectedUser={String(selectedUser)}
            onUserSelect={handleUserSelect}
            device={device}
            profileId={-1}
            os={"windows"}
          />
        )}
        {activeTab === "url-control" && (
          <URLControlTab
            users={users}
            selectedUser={String(selectedUser)}
            onUserSelect={handleUserSelect}
            device={device}
            profileId={-1}
            os={"windows"}
            fromDetails={false}
          />
        )}
      </div>

    </div>
  );
}
